'use client'
import React, { useState, useContext, useEffect } from 'react';
import { createBearerJsonClient } from '../../utils/apiFetchPolicy';
import toast from 'react-hot-toast';
import { useNavigate, useLocation } from "react-router-dom";
import '../../styles/Home.css';
import '../../styles/Login.css';
import { AccountDataContext } from '../../context/AccountContext';
import { useFavorites } from '../../context/FavoritesContext';
import { useCart } from '../../context/CartContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { trackAuth } from '../../utils/analytics';
import { sendNewUserNotification } from '../../utils/emailNotification';
import { API_URL } from '../../config/env';
import { useTranslation } from 'react-i18next';
import { getAuthHttpErrorMessage } from '../../utils/authHttpErrorMessage';
import { resolveAuthReturnPath } from '../../utils/s3MediaAuth';
import PasswordField from '../../components/auth/PasswordField';
import PhoneAuthPanel from '../../components/auth/PhoneAuthPanel';
import SocialLoginButtons from '../../components/auth/SocialLoginButtons';
import { startSocialLogin, fetchSocialProviders, fetchWechatQrUrl } from '../../utils/socialAuthApi';

const api = createBearerJsonClient(null, { baseURL: API_URL });

export const useAccountData = () => {
    const context = useContext(AccountDataContext);
    if (!context) {
        console.error('useAccountData must be used within an AccountDataProvider');
        // Return a default structure to prevent crashes during development
        return {
            setAuth: () => {},
            setToken: () => {},
            auth: null,
            token: null,
            email: null,
            password: null,
            addresses: [],
            setAddresses: () => {},
            paymentMethods: [],
            setPaymentMethods: () => {},
            accountDetails: null,
            setAccountDetails: () => {},
            fetchAccountData: () => {},
            isLoading: false,
        };
    }
    return context;
};

function Register() {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [password1, setPassword1] = useState('');
    const [msg, setMsg] = useState('');
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [showPrivacyNotice, setShowPrivacyNotice] = useState(false);
    const [agreementError, setAgreementError] = useState('');
    const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
    const [registeredEmail, setRegisteredEmail] = useState('');
    const [resending, setResending] = useState(false);
    const [tab, setTab] = useState('email');
    const [providers, setProviders] = useState(null);
    const [wechatQr, setWechatQr] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { setAuth, setToken, fetchAccountData } = useAccountData();
    const { favorites, saveFavorites } = useFavorites();
    const { cart, saveCart } = useCart();
    const { trackClick } = useAnalytics();
    const { t } = useTranslation();

    // Check for pending reservation/commission/service request and show message
    useEffect(() => {
        const pendingReservation = localStorage.getItem('pendingTutoringReservation');
        const pendingServiceReservation = localStorage.getItem('pendingServiceReservation');
        const pendingCommission = localStorage.getItem('pendingCommissionRequest');
        const pendingServiceRequest = localStorage.getItem('pendingServiceRequest');
        if (pendingReservation || location.state?.fromReservation) {
            const message = location.state?.message || 'Please register to link your reservation to your account';
            toast(message, { duration: 5000 });
        } else if (pendingServiceReservation || location.state?.fromServiceReservation) {
            const message = location.state?.message || 'Please register to link your service reservation to your account';
            toast(message, { duration: 5000 });
        } else if (pendingCommission || location.state?.fromCommission) {
            const message = location.state?.message || 'Please register to link your commission request to your account';
            toast(message, { duration: 5000 });
        } else if (pendingServiceRequest || location.state?.fromServiceRequest) {
            const message = location.state?.message || 'Please register to link your service request to your account';
            toast(message, { duration: 5000 });
        } else if (location.state?.message) {
            toast(location.state.message, { duration: 5000 });
        }
        }, [location.state]);

        // Surface any social sign-in error bounced back via ?socialError.
        useEffect(() => {
            const socialError = new URLSearchParams(window.location.search).get('socialError');
            if (socialError) {
                import('../../utils/socialAuthApi').then(({ socialErrorMessage }) =>
                    toast.error(socialErrorMessage(socialError), { duration: 6000 }),
                );
            }
        }, []);

        // Discover which social providers are enabled so we only render their tabs.
        useEffect(() => {
            let cancelled = false;
            fetchSocialProviders()
                .then((res) => !cancelled && setProviders(res))
                .catch(() => !cancelled && setProviders({ google: { enabled: false }, wechat: { enabled: false } }));
            return () => {
                cancelled = true;
            };
        }, []);

        const nextPath = resolveAuthReturnPath(location) || '/account';

        const handleResendConfirmation = async () => {
            try {
                setResending(true);
                await api.post('/auth/resend-confirmation', { email: registeredEmail || email });
                toast.success(t('auth.confirmEmailResent', 'Confirmation email resent — please check your inbox.'));
            } catch (resendErr) {
                toast.error(getAuthHttpErrorMessage(resendErr, t));
            } finally {
                setResending(false);
            }
        };

        const handleWechat = async () => {
            if (providers?.wechat?.qr) {
                const url = await fetchWechatQrUrl({ next: nextPath });
                if (url) {
                    setWechatQr(url);
                    return;
                }
            }
            startSocialLogin('wechat', { next: nextPath });
        };

        const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Reset agreement error
        setAgreementError('');
        
        if (!agreedToTerms) {
            setAgreementError("⚠️ You must agree to the Terms of Service and Privacy Notice to register");
            toast.error("Please agree to the Terms of Service and Privacy Notice to continue");
            return;
        }
        if (!checkPasswordMatch(password, password1)) {
            toast.error("PASSWORD MISMATCH");
            setPassword('');
            setPassword1('');
            return;
        }
        trackClick('Register Submit Button');
        try {
            const response =await api.post('/auth/register', {email, password, name});    
            if (response.data && response.data.needsConfirmation) {
                trackAuth('register');
                trackClick('Register Submit - Success');
                setRegisteredEmail(response.data.email || email);
                setAwaitingConfirmation(true);
                setPassword('');
                setName('');
                toast.success(
                    t('auth.confirmEmailSent', 'Account created — check your email to confirm and finish signing up.'),
                    { duration: 6000 },
                );
            } else if (response.status === 201) {
                const user = response.data.user;  
                toast.success('Login success');
                trackAuth('register');
                trackClick('Register Submit - Success');
                setPassword('');
                setAuth(user);
                setToken(response.data.token);
                
                // Send email notification to admin about new user registration
                sendNewUserNotification({ name, email });
                
                // Fetch account data (profile, addresses, payment methods) from backend.
                // Pass null as the user so fetchAccountData hits the API; passing the
                // registered user object makes it skip the network call and leaves
                // addresses/paymentMethods empty.
                const userId = user._id || user.id;
                if (userId) {
                    fetchAccountData(userId, null);
                }
                saveFavorites(favorites, userId, response.data.token);
                saveCart(cart, userId, response.data.token);

                // Check for pending tutoring reservation and link it to user
                const pendingReservation = localStorage.getItem('pendingTutoringReservation');
                if (pendingReservation) {
                    try {
                        const reservationData = JSON.parse(pendingReservation);
                        if (reservationData.reservationId) {
                            const linkApi = createBearerJsonClient(response.data.token, { baseURL: API_URL });

                            await linkApi.put(`/auth/tutoring-reservations/${reservationData.reservationId}/link-user`, {
                                userId: userId
                            });
                            
                            localStorage.removeItem('pendingTutoringReservation');
                            toast.success('Your reservation has been linked to your account!');
                        }
                    } catch (linkError) {
                        console.error('Error linking reservation to user:', linkError);
                        // Don't fail registration if linking fails
                    }
                }
                
                // Check for pending commission request and link it to user
                const pendingCommission = localStorage.getItem('pendingCommissionRequest');
                if (pendingCommission) {
                    try {
                        const commissionData = JSON.parse(pendingCommission);
                        if (commissionData.commissionId) {
                            const linkApi = createBearerJsonClient(response.data.token, { baseURL: API_URL });

                            await linkApi.put(`/auth/commission-requests/${commissionData.commissionId}/link-user`, {
                                userId: userId
                            });
                            
                            localStorage.removeItem('pendingCommissionRequest');
                            toast.success('Your commission request has been linked to your account!');
                        }
                    } catch (linkError) {
                        console.error('Error linking commission request to user:', linkError);
                        // Don't fail registration if linking fails
                    }
                }
                
                // Check for pending service request and link it to user
                const pendingServiceRequest = localStorage.getItem('pendingServiceRequest');
                if (pendingServiceRequest) {
                    try {
                        const serviceRequestData = JSON.parse(pendingServiceRequest);
                        if (serviceRequestData.requestId) {
                            const linkApi = createBearerJsonClient(response.data.token, { baseURL: API_URL });

                            await linkApi.put(`/auth/service-requests/${serviceRequestData.requestId}/link-user`, {
                                userId: userId
                            });
                            
                            localStorage.removeItem('pendingServiceRequest');
                            toast.success('Your service request has been linked to your account!');
                        }
                    } catch (linkError) {
                        console.error('Error linking service request to user:', linkError);
                        // Don't fail registration if linking fails
                    }
                }
                
                // Check for pending service reservation and link it to user
                const pendingServiceReservation = localStorage.getItem('pendingServiceReservation');
                if (pendingServiceReservation) {
                    try {
                        const reservationData = JSON.parse(pendingServiceReservation);
                        if (reservationData.reservationId) {
                            const linkApi = createBearerJsonClient(response.data.token, { baseURL: API_URL });

                            await linkApi.put(`/auth/service-reservations/${reservationData.reservationId}/link-user`, {
                                userId: userId
                            });
                            
                            localStorage.removeItem('pendingServiceReservation');
                            toast.success('Your service reservation has been linked to your account!');
                        }
                    } catch (linkError) {
                        console.error('Error linking service reservation to user:', linkError);
                        // Don't fail registration if linking fails
                    }
                }

                const returnTo = resolveAuthReturnPath(location);
                if (returnTo) {
                    navigate(returnTo, { replace: true });
                } else {
                    navigate('/account');
                }
            } else {
                trackClick('Register Submit - Error', { error: 'Invalid response' });
                setPassword('');
                setName('');
                setEmail('');
            }
        } catch (error) {
            console.error(error);
            trackClick('Register Submit - Error', { error: error?.message || 'Unknown error' });
            const serverMsg = error?.response?.data?.error;
            if (error?.response?.status === 400 && typeof serverMsg === 'string' && serverMsg.includes('已被注册')) {
                setRegisteredEmail(email);
                toast.error(
                    t('auth.emailAlreadyRegisteredRedirect', 'This email already has an account — taking you to sign in so you can use your password.'),
                    { duration: 6000 },
                );
                window.setTimeout(() => {
                    navigate(`/login?email=${encodeURIComponent(email)}`);
                }, 2500);
            } else {
                toast.error(getAuthHttpErrorMessage(error, t));
            }
        }
    };

    const checkPasswordMatch = (password, confirmPassword) => {
        const messageDiv = document.getElementById("divCheckPasswordMatch");
        // Clear previous messages
        if (confirmPassword.trim() === "") {
            return false; 
        }
        if (password === confirmPassword) {
            setMsg("Passwords match.");
            messageDiv.style.color = "green";
            return true;
        } else {
            setMsg("Passwords do not match.");
            messageDiv.style.color = "red";
            return false;
        }
    };

    return (
        <div className="home">
            <section>
                <div className="flex justify-center items-center" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 100px)', paddingTop: '80px' }}>
                    {awaitingConfirmation ? (
                        <div className="auth-confirm-screen">
                            <h2>{t('auth.confirmEmailTitle', 'Check your email to finish signing up')}</h2>
                            <p style={{ maxWidth: 360 }}>
                                {t('auth.confirmEmailBody', 'We sent a confirmation link to {{email}}. Open it to activate your account, then come back and sign in.', { email: registeredEmail })}
                            </p>
                            <button
                                type="button"
                                onClick={handleResendConfirmation}
                                disabled={resending}
                                style={{ marginTop: '0.75rem', cursor: 'pointer', border: '1px solid var(--brand, #3b82f6)', background: 'transparent', color: 'var(--brand, #3b82f6)', borderRadius: '6px', padding: '0.5rem 1rem', fontWeight: 600 }}
                            >
                                {resending ? t('common.loading', 'Loading...') : t('auth.resendConfirmation', 'Resend confirmation email')}
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate('/login')}
                                style={{ marginTop: '0.5rem', cursor: 'pointer', border: 'none', background: 'none', color: 'var(--muted-foreground, #6b7280)' }}
                            >
                                {t('auth.goToLogin', 'Go to sign in')}
                            </button>
                        </div>
                    ) : (
                        <>
                            <h2>New User</h2>
                            <div className="auth-tabs">
                                <button type="button" className={`auth-tabs__btn ${tab === 'email' ? 'auth-tabs__btn--active' : ''}`} onClick={() => setTab('email')}>{t('auth.email', 'Email')}</button>
                                <button type="button" className={`auth-tabs__btn ${tab === 'phone' ? 'auth-tabs__btn--active' : ''}`} onClick={() => setTab('phone')}>{t('auth.phone', 'Phone')}</button>
                                {providers?.google?.enabled && (
                                    <button type="button" className={`auth-tabs__btn ${tab === 'google' ? 'auth-tabs__btn--active' : ''}`} onClick={() => setTab('google')}>Google</button>
                                )}
                                {providers?.wechat?.enabled && (
                                    <button type="button" className={`auth-tabs__btn ${tab === 'wechat' ? 'auth-tabs__btn--active' : ''}`} onClick={() => setTab('wechat')}>WeChat</button>
                                )}
                            </div>
                            {tab === 'email' && (
                                <form className="login-form">
                        <input
                            type="name"
                            placeholder="Your name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                        />
                        <input
                            type="email"
                            placeholder="Your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                        <PasswordField
                            id="register-password"
                            placeholder="Your pasword"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            autoComplete="new-password"
                        />
                        <PasswordField
                            id="confirm_password"
                            placeholder="Confirm password"
                            value={password1}
                            onChange={(e) => setPassword1(e.target.value)}
                            required
                            autoComplete="new-password"
                        />
                        <div id="divCheckPasswordMatch" >{msg}</div>
                        
                        <div style={{ marginTop: '0.1rem', marginBottom: '1rem' }}>
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer', width: '100%' }}>
                                <input
                                    type="checkbox"
                                    checked={agreedToTerms}
                                    onChange={(e) => {
                                        setAgreedToTerms(e.target.checked);
                                        if (e.target.checked) {
                                            setAgreementError('');
                                        }
                                    }}
                                    required
                                    style={{ 
                                        marginTop: '0.25rem',
                                        width: '10px',
                                        height: '18px',
                                        minWidth: '18px',
                                        flexShrink: 0,
                                        cursor: 'pointer'
                                    }}
                                />
                                <span style={{ flex: 1, lineHeight: '1.5' }}>
                                    I have read and agree to the{' '}
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setShowPrivacyNotice(true);
                                        }}
                                        style={{
                                            color: '#28c7ba',
                                            textDecoration: 'underline',
                                            cursor: 'pointer',
                                            fontSize: 'inherit',
                                            background: 'none',
                                            border: 'none',
                                            padding: 0,
                                            font: 'inherit',
                                            display: 'inline',
                                            marginTop: '0.2rem',
                                            boxShadow: 'none',
                                        }}
                                    >
                                        Terms of Service and Privacy Notice.
                                    </button>
                                </span>
                            </label>
                            {agreementError && (
                                <div 
                                    id="divAgreementError"
                                    style={{
                                        color: 'red',
                                        fontSize: '0.9rem',
                                        marginTop: '0.5rem',
                                        marginLeft: '1.5rem',
                                        fontWeight: 500,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}
                                >
                                    {agreementError}
                                </div>
                            )}
                        </div>
                        
                        <button type="button" color='orange' onClick={handleSubmit}>Register</button>
                        </form>
                    )}

                    {tab === 'phone' && (
                        <PhoneAuthPanel mode="register" next={nextPath} />
                    )}

                    {tab === 'google' && (
                        <div className="social-login">
                            <button type="button" className="social-login__btn social-login__btn--google" onClick={() => startSocialLogin('google', { next: nextPath })}>
                                <span>Continue with Google</span>
                            </button>
                        </div>
                    )}

                    {tab === 'wechat' && (
                        <div className="social-login">
                            {wechatQr ? (
                                <div className="social-login__qr" role="dialog" aria-label="Scan with WeChat to sign in">
                                    <iframe title="WeChat sign-in" src={wechatQr} width="280" height="360" frameBorder="0" scrolling="no" />
                                </div>
                            ) : (
                                <button type="button" className="social-login__btn social-login__btn--wechat" onClick={handleWechat}>
                                    <span>Continue with WeChat</span>
                                </button>
                            )}
                        </div>
                    )}
                    </>)}

                    {showPrivacyNotice && (
                        <div
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 1000,
                                padding: '2rem'
                            }}
                            onClick={() => setShowPrivacyNotice(false)}
                        >
                            <div
                                style={{
                                    backgroundColor: 'white',
                                    borderRadius: '12px',
                                    padding: '2rem',
                                    maxWidth: '600px',
                                    maxHeight: '80vh',
                                    overflowY: 'auto',
                                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                                    <h2 style={{ margin: 0 }}>Terms of Service and Privacy Notice</h2>
                                    <button
                                        onClick={() => setShowPrivacyNotice(false)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            fontSize: '1.5rem',
                                            cursor: 'pointer',
                                            color: '#6c757d'
                                        }}
                                    >
                                        ×CLOSE
                                    </button>
                                </div>
                                
                                <div style={{ lineHeight: '1.6', color: '#2c3e50' }}>
                                    <h3>1. Terms of Service</h3>
                                    <p>
                                        By accessing and using this website, you accept and agree to be bound by the terms and provision of this agreement.
                                    </p>
                                    <p>
                                        You agree to use this website only for lawful purposes and in a way that does not infringe the rights of, restrict or inhibit anyone else's use and enjoyment of the website.
                                    </p>
                                    
                                    <h3 style={{ marginTop: '1.5rem' }}>2. Privacy Notice</h3>
                                    <p>
                                        <strong>Information We Collect:</strong> We collect information that you provide directly to us, including your name, email address, password, and any other information you choose to provide when registering or using our services.
                                    </p>
                                    <p>
                                        <strong>How We Use Your Information:</strong> We use the information we collect to:
                                    </p>
                                    <ul>
                                        <li>Provide, maintain, and improve our services</li>
                                        <li>Process transactions and send related information</li>
                                        <li>Send you technical notices and support messages</li>
                                        <li>Respond to your comments and questions</li>
                                    </ul>
                                    
                                    <p>
                                        <strong>Data Security:</strong> We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.
                                    </p>
                                    
                                    <p>
                                        <strong>Your Rights:</strong> You have the right to access, update, or delete your personal information at any time by contacting us or through your account settings.
                                    </p>
                                    
                                    <p>
                                        <strong>Cookies:</strong> We use cookies and similar tracking technologies to track activity on our website and hold certain information to improve your experience.
                                    </p>
                                    
                                    <p>
                                        <strong>Third-Party Services:</strong> We may use third-party services that collect, monitor, and analyze information to help us improve our services.
                                    </p>
                                    
                                    <p style={{ marginTop: '1.5rem', fontSize: '0.9rem', color: '#6c757d' }}>
                                        Last updated: {new Date().toLocaleDateString()}
                                    </p>
                                </div>
                                
                                <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                                    <button
                                        onClick={() => {
                                            setAgreedToTerms(true);
                                            setShowPrivacyNotice(false);
                                        }}
                                        style={{
                                            backgroundColor: '#28c7ba',
                                            color: 'white',
                                            border: 'none',
                                            padding: '0.75rem 1.5rem',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontSize: '1rem',
                                            fontWeight: 600
                                        }}
                                    >
                                        I Agree
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    </div>
                </section>
            </div>
    );
}

export default Register; 


